import React, { useEffect, useState } from 'react';
import { BoxStyled, TypographyStyledHeader } from '../patientInformation/style';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { useDispatch } from 'react-redux';
import { setCompareData } from '../../store/slices/metaDataSlice';
import { NavigationButtons } from '../../shared/components/navigationButtons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  StyledTableCell,
  StyledTableRow,
  TypographyBg20BlueBold,
  TypographyCalibri12Bold,
  TypographyCalibri12Normal
} from './style';

import { TileSleekGridGroup } from '../../shared/components/tileSleekGridGroup';
import EmailResultsIcon from '../../assets/icons/emailResults.svg';
import DownloadIcon from '../../assets/icons/downloadIcon.svg';
import { compareScreenTileData } from '../../shared/shared.constants';
import {
  handleDownloadPdf,
  handleSharePdf,
  roundToPrecision
} from '../../shared/utils/utilFunctions';

const TableView = () => {
  // Always use the same keys so that the Accordion remains controlled.
  const [expandedAccordions, setExpandedAccordions] = useState({
    nutrients: true,
    vitamins: false,
    vitamins2: false,
    minerals: false,
    minerals2: false
  });
  // For showing a loading state if needed
  const [loading, setLoading] = useState(false);
  const { compare } = useAppSelector(metaDataSelector);
  const {
    selectedProducts,
    p1VolumeCalculationOffset,
    p2VolumeCalculationOffset
  } = compare;

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordions({ ...expandedAccordions, [panel]: isExpanded });
  };

  // Function to scale nutrient values based on user-provided volume
  const scaleNutrientValues = (product: any, userVolume: number) => {
    const originalVolume = parseFloat(product.volumeOffset); // e.g., "220 mL" -> 220

    if (userVolume === originalVolume) {
      return JSON.parse(JSON.stringify(product));
    }

    const scalingFactor = userVolume / originalVolume;

    const scaledProduct = JSON.parse(JSON.stringify(product)); // Deep clone the product

    const nonScaledNutrients = [
      'water',
      'osmolality',
      'osmolarity',
      'renalSoluteLoad'
    ];

    const scaleNestedValues = (obj: any, category?: string) => {
      for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (category === 'minerals' && nonScaledNutrients.includes(key)) {
            continue;
          }
          scaleNestedValues(obj[key]); // Recursively scale nested objects
        } else if (key === 'value') {
          if (obj[key] === 'N/A' || isNaN(Number(obj[key]))) {
            continue;
          }

          obj[key] = roundToPrecision(Number(obj[key] * scalingFactor)); // Scale and round to 2 decimal places
        }
      }
    };

    scaleNestedValues(scaledProduct.macroNutrients, 'macroNutrients');
    scaleNestedValues(scaledProduct.vitamins, 'vitamins');
    scaleNestedValues(scaledProduct.minerals, 'minerals');
    return scaledProduct;
  };

  const renderComparisonTableNutrients = () => {
    if (selectedProducts.length !== 2) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts[1];

    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;

    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = scaleNutrientValues(product2, userVolume2);

    const nutrients = [
      { label: 'Energy', key: 'energy', units: ['kj', 'kcal'] },
      { label: 'Protein', key: 'protein' },
      {
        label: 'Carbohydrate',
        key: 'carbohydrate',
        subKeys: ['ofWhichSugars']
      },
      { label: 'Fat', key: 'fat', subKeys: ['ofWhichSaturates'] },
      { label: 'Fibre', key: 'fibre' },
      { label: 'CaHMB', key: 'cahmb' },
      { label: 'Salt', key: 'salt' }
    ];

    return (
      <div
        id="section-macronutrients"
        style={{ background: '#fff' }}
        className="pdf-section"
      >
        <Accordion
          id="nutrients"
          expanded={expandedAccordions.nutrients}
          onChange={handleAccordionChange('nutrients')}
          sx={{
            marginTop: '0.625rem',
            marginBottom: '0px',
            boxShadow: 'none',
            border: '0.0625rem solid #8BB5C6',
            breakInside: 'avoid'
          }}
          className="accordion-pdf-spacing"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TypographyBg20BlueBold>MACRONUTRIENTS</TypographyBg20BlueBold>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0' }}>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 'none', border: 'none' }}
            >
              <Table>
                <TableHead>
                  <TableRow className="pdf-row">
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        Unit
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        {product1.name} ({userVolume1} mL)
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                      <TypographyCalibri12Bold
                        sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          width: 'auto'
                        }}
                      >
                        {product2.name} ({userVolume2} mL)
                      </TypographyCalibri12Bold>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nutrients.map((nutrient) => {
                    const product1Nutrient =
                      scaledProduct1.macroNutrients[nutrient.key];
                    const product2Nutrient =
                      scaledProduct2.macroNutrients[nutrient.key];

                    if (nutrient.key === 'energy') {
                      // Special handling for energy
                      return (
                        <React.Fragment key={nutrient.key}>
                          {nutrient.units.map((unit, index) => (
                            <StyledTableRow key={unit}>
                              <StyledTableCell>
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'left',
                                    width: 'auto'
                                  }}
                                >
                                  {index === 0 ? nutrient.label : ''}{' '}
                                </TypographyCalibri12Normal>
                                {/* Show label only for the first row */}
                              </StyledTableCell>
                              <StyledTableCell>
                                {' '}
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product1Nutrient[unit].unit}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ backgroundColor: '#DEF3FA' }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product1Nutrient[unit].value}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ backgroundColor: '#CCE4F5' }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product2Nutrient[unit].value}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={nutrient.key}>
                        {/* Main Nutrient Row */}
                        <StyledTableRow>
                          <StyledTableCell>
                            {' '}
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'left',
                                width: 'auto'
                              }}
                            >
                              {nutrient.label}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product1Nutrient.unit}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product1Nutrient.value}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                          <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                            <TypographyCalibri12Normal
                              sx={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: 'auto'
                              }}
                            >
                              {product2Nutrient.value}
                            </TypographyCalibri12Normal>
                          </StyledTableCell>
                        </StyledTableRow>

                        {/* Sub-Nutrient Rows */}
                        {nutrient.subKeys?.map((subKey) => {
                          const product1SubNutrient = product1Nutrient[subKey];
                          const product2SubNutrient = product2Nutrient[subKey];

                          return (
                            <StyledTableRow key={subKey}>
                              <StyledTableCell
                                sx={{
                                  paddingLeft: '2.5rem',
                                  minWidth: '130px'
                                }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'left',
                                    width: 'auto'
                                  }}
                                >
                                  {`of which ${subKey.replace('ofWhich', '').toLowerCase()}`}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell>
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product1SubNutrient.unit}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ backgroundColor: '#DEF3FA' }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product1SubNutrient.value}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ backgroundColor: '#CCE4F5' }}
                              >
                                <TypographyCalibri12Normal
                                  sx={{
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    width: 'auto'
                                  }}
                                >
                                  {product2SubNutrient.value}
                                </TypographyCalibri12Normal>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  const renderComparisonTableVitamins = () => {
    if (selectedProducts.length !== 2) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts[1];

    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;

    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = scaleNutrientValues(product2, userVolume2);

    const vitamins = [
      {
        label: 'Vitamin A (RE)',
        key: 'vitaminAre',
        subKeys: [
          { key: 'ofWhichBCaroteneRE', label: 'of which ß-carotene (RE)' }
        ]
      },
      { label: 'Vitamin D', key: 'vitaminD' },
      { label: 'Vitamin E (α-TE)', key: 'vitaminE' },
      { label: 'Vitamin K<sup>*</sup>', key: 'vitaminK' },
      { label: 'Vitamin C', key: 'vitaminC' },
      { label: 'Folate<sup>**</sup>', key: 'folate' },
      { label: 'Folic Acid', key: 'folacin' },
      { label: 'Thiamin (vitamin B<sub>1</sub>)', key: 'thiaminVitaminB1' },
      {
        label: 'Riboflavin (vitamin B<sub>2</sub>)',
        key: 'riboflavinVitaminB2'
      },
      { label: 'Vitamin B<sub>6</sub><sup>¥</sup>', key: 'vitaminB6' },
      { label: 'Vitamin B<sub>12</sub>', key: 'vitaminB12' },
      { label: 'Niacin<sup>^</sup> NE', key: 'niacin' },
      { label: 'Pantothenic Acid<sup>†</sup>', key: 'panthothenicAcid' },
      { label: 'Biotin<sup>±</sup>', key: 'biotin' }
    ];

    return (
      <>
        <div
          id="section-vitamins"
          style={{ background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="vitamins"
            expanded={expandedAccordions.vitamins}
            onChange={handleAccordionChange('vitamins')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '0.625rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>VITAMINS</TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0' }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product1.name} ({userVolume1} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product2.name} ({userVolume2} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vitamins.map((vitamin, index) => {
                      const product1Nutrient =
                        scaledProduct1.vitamins[vitamin.key];
                      const product2Nutrient =
                        scaledProduct2.vitamins[vitamin.key];

                      return (
                        <React.Fragment key={vitamin.key}>
                          {/* Main Nutrient Row */}
                          <StyledTableRow
                            className={index > 7 ? 'pdf-hidden-element' : ''}
                          >
                            <StyledTableCell>
                              {' '}
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'left',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: vitamin.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.unit}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product2Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>

                          {/* Sub-Nutrient Rows */}
                          {vitamin.subKeys?.map((subKey) => {
                            const product1SubNutrient =
                              product1Nutrient[subKey.key];
                            const product2SubNutrient =
                              product2Nutrient[subKey.key];

                            return (
                              <StyledTableRow key={subKey.key}>
                                <StyledTableCell
                                  sx={{
                                    paddingLeft: '2.5rem',
                                    minWidth: '130px'
                                  }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {subKey.label}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell>
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product1SubNutrient.unit}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell
                                  sx={{ backgroundColor: '#DEF3FA' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product1SubNutrient.value}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell
                                  sx={{ backgroundColor: '#CCE4F5' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product2SubNutrient.value}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
        <div
          id="section-vitamins-2"
          style={{ display: 'none', background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            expanded={expandedAccordions.vitamins2}
            onChange={handleAccordionChange('vitamins2')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '0.625rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>VITAMINS</TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0' }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product1.name} ({userVolume1} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product2.name} ({userVolume2} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vitamins.map((vitamin, index) => {
                      if (index < 8) return;
                      const product1Nutrient =
                        scaledProduct1.vitamins[vitamin.key];
                      const product2Nutrient =
                        scaledProduct2.vitamins[vitamin.key];

                      return (
                        <React.Fragment key={vitamin.key}>
                          {/* Main Nutrient Row */}
                          <StyledTableRow>
                            <StyledTableCell>
                              {' '}
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: vitamin.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.unit}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product2Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>

                          {/* Sub-Nutrient Rows */}
                          {vitamin.subKeys?.map((subKey) => {
                            const product1SubNutrient =
                              product1Nutrient[subKey.key];
                            const product2SubNutrient =
                              product2Nutrient[subKey.key];

                            return (
                              <StyledTableRow key={subKey.key}>
                                <StyledTableCell sx={{ paddingLeft: '35px' }}>
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {subKey.label}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell>
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product1SubNutrient.unit}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell
                                  sx={{ backgroundColor: '#DEF3FA' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product1SubNutrient.value}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                                <StyledTableCell
                                  sx={{ backgroundColor: '#CCE4F5' }}
                                >
                                  <TypographyCalibri12Normal
                                    sx={{
                                      verticalAlign: 'middle',
                                      textAlign: 'center',
                                      width: 'auto'
                                    }}
                                  >
                                    {product2SubNutrient.value}
                                  </TypographyCalibri12Normal>
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
      </>
    );
  };

  const renderComparisonTableMinerals = () => {
    if (selectedProducts.length !== 2) return null;

    const product1 = selectedProducts[0];
    const product2 = selectedProducts[1];

    const userVolume1 = p1VolumeCalculationOffset;
    const userVolume2 = p2VolumeCalculationOffset;

    const scaledProduct1 = scaleNutrientValues(product1, userVolume1);
    const scaledProduct2 = scaleNutrientValues(product2, userVolume2);

    const minerals = [
      { label: 'Sodium', key: 'sodium' },
      { label: 'Potassium', key: 'potassium' },
      { label: 'Magnesium', key: 'magnesium' },
      { label: 'Phosphorus', key: 'phosphate' },
      { label: 'Calcium', key: 'calcium' },
      { label: 'Chloride', key: 'chloride' },
      { label: 'Iron', key: 'iron' },
      { label: 'Zinc', key: 'zinc' },
      { label: 'Manganese', key: 'manganese' },
      { label: 'Copper', key: 'copper' },
      { label: 'Iodine', key: 'iodine' },
      { label: 'Selenium', key: 'selenium' },
      { label: 'Chromium', key: 'chromium' },
      { label: 'Molybdenum<sup>‡</sup>', key: 'molybdenum' },
      { label: 'Taurine', key: 'taurine' },
      { label: 'Water', key: 'water' },
      { label: 'Osmolarity', key: 'osmolarity' },
      { label: 'Osmolality', key: 'osmolality' },
      { label: 'Renal solute load', key: 'renalSoluteLoad' }
    ];

    return (
      <>
        <div
          id="section-minerals-1"
          style={{ background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="minerals"
            expanded={expandedAccordions.minerals}
            onChange={handleAccordionChange('minerals')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '1.25rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>
                MINERALS AND OTHER
              </TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0' }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product1.name} ({userVolume1} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product2.name} ({userVolume2} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {minerals.map((mineral, index) => {
                      const product1Nutrient =
                        scaledProduct1.minerals[mineral.key];
                      const product2Nutrient =
                        scaledProduct2.minerals[mineral.key];

                      return (
                        <React.Fragment key={mineral.key}>
                          {/* Main Nutrient Row */}
                          <StyledTableRow
                            className={index > 9 ? 'pdf-hidden-element' : ''}
                          >
                            <StyledTableCell sx={{ minWidth: '130px' }}>
                              {' '}
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'left',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: mineral.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: product1Nutrient.unit
                                  }}
                                ></span>
                                {/* {product1Nutrient.unit} */}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product2Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
        <div
          id="section-minerals-2"
          style={{ display: 'none', background: '#fff' }}
          className="pdf-section"
        >
          <Accordion
            id="minerals-2"
            expanded={expandedAccordions.minerals2}
            onChange={handleAccordionChange('minerals2')}
            sx={{
              marginTop: '0.625rem',
              marginBottom: '1.25rem',
              boxShadow: 'none',
              border: '0.0625rem solid #8BB5C6',
              breakInside: 'avoid'
            }}
            className="accordion-pdf-spacing"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <TypographyBg20BlueBold>
                MINERALS AND OTHER
              </TypographyBg20BlueBold>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0' }}>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', border: 'none' }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          Unit
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#DEF3FA' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product1.name} ({userVolume1} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                      <StyledTableCell sx={{ backgroundColor: '#CCE4F5' }}>
                        <TypographyCalibri12Bold
                          sx={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 'auto'
                          }}
                        >
                          {product2.name} ({userVolume2} mL)
                        </TypographyCalibri12Bold>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {minerals.map((mineral, index) => {
                      if (index < 10) return;
                      const product1Nutrient =
                        scaledProduct1.minerals[mineral.key];
                      const product2Nutrient =
                        scaledProduct2.minerals[mineral.key];

                      return (
                        <React.Fragment key={mineral.key}>
                          {/* Main Nutrient Row */}
                          <StyledTableRow>
                            <StyledTableCell>
                              {' '}
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: mineral.label
                                  }}
                                ></span>
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell>
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.unit}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#DEF3FA' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product1Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{ backgroundColor: '#CCE4F5' }}
                            >
                              <TypographyCalibri12Normal
                                sx={{
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  width: 'auto'
                                }}
                              >
                                {product2Nutrient.value}
                              </TypographyCalibri12Normal>
                            </StyledTableCell>
                          </StyledTableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
      </>
    );
  };

  const handlePDFGeneration = async (
    type,
    filename,
    pdfName = 'Products Comparison'
  ) => {
    setLoading(true);
    const currentStates = { ...expandedAccordions };
    // Expand all accordions
    setExpandedAccordions({
      nutrients: true,
      vitamins: true,
      vitamins2: true,
      minerals: true,
      minerals2: true
    });

    try {
      if (type === 'download') {
        await handleDownloadPdf(filename, 'Products Comparison');
      } else {
        await handleSharePdf(filename, pdfName, 'Products Comparison');
      }

      // Collapse accordions
      setExpandedAccordions(currentStates);

      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      // console.error(error);
      setLoading(false);
    }
  };

  return (
    <>
      <BoxStyled>
        <TypographyStyledHeader sx={{ marginTop: '1.25rem' }}>
          PRODUCT COMPARISON
        </TypographyStyledHeader>
        <div
          id="pdf-main-content"
          style={{ marginTop: '1.875rem', width: '100%' }}
        >
          {renderComparisonTableNutrients()}
          {renderComparisonTableVitamins()}
          {renderComparisonTableMinerals()}
        </div>

        <TileSleekGridGroup tileData={compareScreenTileData} />

        <Button
          variant="contained"
          onClick={() =>
            handlePDFGeneration(
              'share',
              'Products Comparison.pdf',
              'Products Comparison'
            )
          }
          disabled={false}
          endIcon={<img src={EmailResultsIcon} alt="arrow" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '0.625rem',
            marginTop: '1.25rem',
            background: '#007acc',
            ':disabled': { color: 'black' }
          }}
          data-testid="pdf-share-button"
        >
          EMAIL RESULTS
        </Button>

        <Button
          variant="contained"
          onClick={() =>
            handlePDFGeneration('download', 'Products Comparison.pdf')
          }
          disabled={false}
          endIcon={<img src={DownloadIcon} alt="arrow" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '0.625rem',
            background: '#007acc',
            ':disabled': { color: 'black' }
          }}
          data-testid="pdf-download-button"
        >
          DOWNLOAD
        </Button>
      </BoxStyled>
      <BoxStyled sx={{ marginTop: '3.125rem' }}>
        <NavigationButtons
          nextDisabled={true}
          nextRoute={'/compare/table'}
          nextVisible={false}
        />
      </BoxStyled>
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#007ACC',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress
          color="inherit"
          size={60}
          thickness={5}
          sx={{ animationDuration: '800ms' }}
        />
        <TypographyBg20BlueBold sx={{ mt: 2 }}>
          Generating PDF ...
        </TypographyBg20BlueBold>
      </Backdrop>
    </>
  );
};

export { TableView };
